import React from 'react';
import { CheckCircle, Gift } from 'lucide-react';
import { ProductPlacementEntry } from '../../types';

interface VisitPlacementsSectionProps {
  placements: Record<string, ProductPlacementEntry>;
  resolveProductName: (productId: string) => string;
}

const PlacementFlag: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-1 text-xs">
    <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
    <span className="text-gray-700">{label}</span>
  </div>
);

const facingsSuffix = (count?: number) => (count && count > 0 ? ` (${count} facings)` : '');

export const VisitPlacementsSection: React.FC<VisitPlacementsSectionProps> = ({
  placements,
  resolveProductName,
}) => {
  const entries = Object.entries(placements);
  if (entries.length === 0) return null;

  return (
    <div className="bg-brand-50 p-4 rounded-xl border border-brand-200">
      <div className="flex items-center gap-2 mb-3">
        <Gift size={18} className="text-brand-600" />
        <h3 className="font-bold text-brand-900">Product Placements</h3>
      </div>

      <div className="space-y-3">
        {entries.map(([productId, data]) => (
          <div key={productId} className="bg-white p-4 rounded-lg border-2 border-brand-200">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <p className="font-bold text-base text-brand-900">{resolveProductName(productId)}</p>
              <div className="flex items-center gap-2">
                {data.secured === true && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-bold">
                    Placement Secured
                  </span>
                )}
                {data.secured === false && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-bold">
                    Not secured
                  </span>
                )}
                {data.is_new_introduction && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold">
                    New Introduction
                  </span>
                )}
              </div>
            </div>

            {data.placements && (
              <div className="grid grid-cols-2 gap-2">
                {data.placements.general_shelf && <PlacementFlag label="General Shelf" />}
                {(Number(data.placements.fridge_facings_count) > 0 ||
                  data.placements.fridge_placement) && (
                  <PlacementFlag
                    label={`Fridge${facingsSuffix(data.placements.fridge_facings_count)}`}
                  />
                )}
                {(Number(data.placements.dry_shelf_facings_count) > 0 ||
                  data.placements.dry_shelf_placement) && (
                  <PlacementFlag
                    label={`Dry Shelf${facingsSuffix(data.placements.dry_shelf_facings_count)}`}
                  />
                )}
                {data.placements.poster_placement && <PlacementFlag label="Poster" />}
                {data.placements.display_stand && <PlacementFlag label="Display Stand" />}
                {data.placements.checkout_placement && <PlacementFlag label="Checkout" />}
              </div>
            )}

            {data.is_new_introduction && data.introduction && (
              <div
                className="mt-3 pt-3 border-t border-amber-200 bg-amber-50 p-3 rounded-lg
                  space-y-2 text-sm"
              >
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-600">Samples:</span>{' '}
                    <strong>{data.introduction.samples_quantity}</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">Presentation:</span>{' '}
                    <strong>
                      {data.introduction.presentation_type === 'full_presentation'
                        ? 'Full'
                        : 'Samples Only'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-gray-600">Decision Maker:</span>{' '}
                    <strong>{data.introduction.decision_maker_present ? 'Yes' : 'No'}</strong>
                  </div>
                  <div>
                    <span className="text-gray-600">Interest:</span>{' '}
                    <strong
                      className={
                        data.introduction.interest_level === 'hot'
                          ? 'text-red-600'
                          : data.introduction.interest_level === 'warm'
                            ? 'text-amber-600'
                            : 'text-blue-600'
                      }
                    >
                      {data.introduction.interest_level?.toUpperCase()}
                    </strong>
                  </div>
                </div>
                {data.introduction.materials_left &&
                  data.introduction.materials_left.length > 0 && (
                    <div>
                      <span className="text-gray-600">Materials:</span>{' '}
                      {data.introduction.materials_left.map((m) => (
                        <span
                          key={m}
                          className="inline-block px-2 py-0.5 bg-white border rounded text-xs mr-1"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
